import { useState, useEffect, useMemo, useRef, SyntheticEvent, ReactNode } from 'react';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import { toast } from 'react-toastify';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Autocomplete from '@mui/material/Autocomplete';
import Skeleton from '@mui/material/Skeleton';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import EastIcon from '@mui/icons-material/East';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { MAP_COLOR_CODE_WITH_COLOR_HEXA } from 'constant/ViewConstant';
import { CONDITION_PHRASE } from 'pages/SystemTenant/AROView/InsightViewDefinition/components/ModalCellColorRule';
import {
  useGetLayerListDropdownByCube,
  useGetCubeTimeInterval,
  useGetCubeDataDetailOptions,
} from 'services/v1/Tenant/AROCube/CubeDataEntryService';
import { useGetFunctionDefinitionCubeDropdown } from 'services/v1/SystemTenant/AROFunction/FunctionDefinitionService';
import { useGetCubeDefinitionDetails } from 'services/v1/SystemTenant/AROCube/CubeBuilderService';
import { useGetInsightViewCubeAttributesDropdown } from 'services/v1/Tenant/AROCube/CubeInsightViewService';
import { useGetStandardViewCubeAttributesDropdown } from 'services/v1/SystemTenant/AROView/StandardViewDefinitionService';
import { useSubjectDropdown, useGetSubjectDetails } from 'services/v1/SystemTenant/AROKMS/SubjectService';
import { useGetFunctionDefinitionSubjectAttributeDropdown } from 'services/v1/SystemTenant/AROFunction/FunctionDefinitionService';
import { RULE_TYPE_PRICE_FROM_SUBJECT, RULE_TYPE_NUMBER_FROM_SUBJECT } from 'constant/RuleConstant';
import {
  EffectiveCellColorItem,
  EffectiveCellColorRuleItem,
  useGetEffectiveCellColors,
  useUpsertCellColorOverride,
} from 'services/v1/Tenant/AROCube/CubeInsightViewCellColorService';
import { ModalCellColorOverride } from './ModalCellColorOverride';
import { ModalDataFlowDefinition } from './ModalDataFlowDefinition';

interface TargetSubjectOption extends AutoCompleteItem {
  metaDataId?: string;
}

export interface DecisionPointPayload {
  target: {
    subjectId: string;
    tableDefinitionId: string;
  };
  source: {
    cubeAttributeId: string;
    cubeDefinitionId: string;
    cubeLayerId: string;
    timeIntervalId: string;
    timePeriodValue: string;
  };
}

interface ModalDecisionPointProps {
  open: boolean;
  onClose: () => void;
  cubeDefinitionId?: string;
  insightViewId?: string;
  // When set (e.g. on a subject data-input page), the target subject is fixed
  // to this id and the Target Subject dropdown is hidden.
  targetSubjectId?: string;
  // The view's currently selected measure; a new cell colour preselects it.
  activeCubeAttributeId?: string;
}

const modalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 900,
  maxWidth: '95vw',
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  borderRadius: 1,
  px: 3,
  py: 2,
  outline: 'none',
};

const filterAutoCompleteStyle = {
  flex: 1,
  '& .MuiOutlinedInput-root': {
    padding: '1px 2px',
  },
};

const sectionBoxStyle = {
  bgcolor: '#FBFBFB',
  py: 1.5,
  px: 1.5,
  borderRadius: 1,
  border: '1px solid #E3EBF6',
};

const stepBadgeStyle = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  bgcolor: '#3B4797',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

// Renders a selection value inside the decision summary sentence.
function SummaryToken(props: { label?: string | null }) {
  const { label } = props;
  if (!label) {
    return (
      <Box component='span' sx={{ fontStyle: 'italic', color: '#98A2AE' }}>
        not selected
      </Box>
    );
  }
  return (
    <Box component='span' sx={{ fontWeight: 700, color: '#3B4797' }}>
      {label}
    </Box>
  );
}

// A cell colour rule whose numeric range is editable inline (colour/condition stay fixed).
interface EditableRangeRule {
  id: string | null;
  color: AutoCompleteItem | null;
  condition: AutoCompleteItem | null;
  attributeReference: AutoCompleteItem | null;
  value: string;
  minValue: string;
  maxValue: string;
  includeMin: boolean;
  includeMax: boolean;
  priorityOrder: number;
}

const isBetweenRangeRule = (rule: EditableRangeRule) => rule.condition?.value === 'BETWEEN';

const GREATER_CONDITIONS = new Set(['GREATER_THAN', 'NUMERIC_GREATER_THAN_OR_EQUAL']);
const LESS_CONDITIONS = new Set(['LESS_THAN', 'NUMERIC_LESS_THAN_OR_EQUAL']);

type BandKind = 'top' | 'middle' | 'bottom';

interface LadderBand {
  index: number;
  rule: EditableRangeRule;
  kind: BandKind;
}

// The lower boundary value of a band (empty for the open-ended bottom band).
function bandLowerBound(rule: EditableRangeRule): string {
  const condition = rule.condition?.value as string | undefined;
  if (condition === 'BETWEEN') return rule.minValue;
  if (condition && GREATER_CONDITIONS.has(condition)) return rule.value;
  return '';
}

// The upper boundary value of a band (empty for the open-ended top band).
function bandUpperBound(rule: EditableRangeRule): string {
  const condition = rule.condition?.value as string | undefined;
  if (condition === 'BETWEEN') return rule.maxValue;
  if (condition && LESS_CONDITIONS.has(condition)) return rule.value;
  return '';
}

// Detect a traffic-light ladder: one "above" rule on top, any number of contiguous
// "between" bands, one "below" rule at the bottom, sharing boundary values. Lets the
// whole set be edited as a list of breakpoints. Returns bands top→bottom, or null.
function detectBandLadder(rules: EditableRangeRule[]): LadderBand[] | null {
  if (rules.length < 2) return null;
  const sorted = rules
    .map((rule, index) => ({ rule, index }))
    .sort((a, b) => a.rule.priorityOrder - b.rule.priorityOrder);
  const bands: LadderBand[] = [];
  for (let pos = 0; pos < sorted.length; pos += 1) {
    const { rule, index } = sorted[pos];
    if (rule.attributeReference) return null;
    const condition = rule.condition?.value as string | undefined;
    if (!condition) return null;
    if (pos === 0) {
      if (!GREATER_CONDITIONS.has(condition)) return null;
      bands.push({ index, rule, kind: 'top' });
    } else if (pos === sorted.length - 1) {
      if (!LESS_CONDITIONS.has(condition)) return null;
      bands.push({ index, rule, kind: 'bottom' });
    } else {
      if (condition !== 'BETWEEN') return null;
      bands.push({ index, rule, kind: 'middle' });
    }
  }
  // Adjacent bands must share a boundary value (when both are filled in).
  for (let i = 0; i < bands.length - 1; i += 1) {
    const lower = bandLowerBound(bands[i].rule);
    const upper = bandUpperBound(bands[i + 1].rule);
    if (lower !== '' && upper !== '') {
      const a = parseFloat(lower);
      const b = parseFloat(upper);
      if (!Number.isNaN(a) && !Number.isNaN(b) && a !== b) return null;
    }
  }
  return bands;
}

// A single colour band row: swatch, colour name, then a description/control.
function CellColorBandRow(props: { color: AutoCompleteItem | null; children: ReactNode }) {
  const { color, children } = props;
  const hexa = color ? MAP_COLOR_CODE_WITH_COLOR_HEXA.get(color.value.toString()) : undefined;
  return (
    <Stack
      direction='row'
      alignItems='center'
      spacing={1}
      sx={{ px: 1.5, py: 1, bgcolor: '#fff', border: '1px solid #E3EBF6', borderRadius: 1 }}
    >
      <Tooltip title={color?.label ?? ''}>
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            bgcolor: hexa ?? '#E0E0E0',
            border: '1px solid rgba(0, 0, 0, 0.15)',
            flexShrink: 0,
          }}
        />
      </Tooltip>
      <Typography variant='body2' sx={{ color: '#475467', fontWeight: 600, minWidth: 56 }}>
        {color?.label}
      </Typography>
      <Stack direction='row' alignItems='center' spacing={0.75} flex={1} justifyContent='flex-end'>
        {children}
      </Stack>
    </Stack>
  );
}

const toEditableRangeRule = (rule: EffectiveCellColorRuleItem): EditableRangeRule => ({
  id: rule.id,
  color: rule.color,
  condition: rule.condition,
  attributeReference: rule.attributeReference,
  value: rule.value != null ? String(rule.value) : '',
  minValue: rule.minValue != null ? String(rule.minValue) : '',
  maxValue: rule.maxValue != null ? String(rule.maxValue) : '',
  includeMin: !!rule.includeMin,
  includeMax: !!rule.includeMax,
  priorityOrder: rule.priorityOrder,
});

const isOptionEqual = (option: AutoCompleteItem, value: AutoCompleteItem) =>
  option.value?.toString() === value.value?.toString();

// Numeric attribute rule types (currency, number, percentage, integer variants).
const NUMERIC_ATTRIBUTE_RULE_TYPES = new Set<string>([
  ...RULE_TYPE_PRICE_FROM_SUBJECT,
  ...RULE_TYPE_NUMBER_FROM_SUBJECT,
]);

export function ModalDecisionPoint({
  open,
  onClose,
  cubeDefinitionId,
  insightViewId,
  targetSubjectId,
  activeCubeAttributeId,
}: ModalDecisionPointProps) {
  // Insight view pages show the Cell Colour feature; subject pages (no insight view) show Definition.
  const showCellColour = !!insightViewId;

  // ── Source selections ──
  const [sourceCube, setSourceCube] = useState<AutoCompleteItem | null>(null);
  const [sourceLayer, setSourceLayer] = useState<AutoCompleteItem | null>(null);
  const [sourceTimeInterval, setSourceTimeInterval] = useState<AutoCompleteItem | null>(null);
  const [sourceAttribute, setSourceAttribute] = useState<AutoCompleteItem | null>(null);
  const [sourceTimePeriod, setSourceTimePeriod] = useState<AutoCompleteItem | null>(null);

  // ── Target selections ──
  const [targetSubject, setTargetSubject] = useState<TargetSubjectOption | null>(null);
  const [targetTableDefinition, setTargetTableDefinition] = useState<AutoCompleteItem | null>(null);

  const [showPayload, setShowPayload] = useState(false);
  const [copied, setCopied] = useState(false);
  const [dataFlowModalOpen, setDataFlowModalOpen] = useState(false);

  // ── Cell colour override ──
  const [cellColorModal, setCellColorModal] = useState<{ visible: boolean; data?: EffectiveCellColorItem }>({
    visible: false,
  });
  // Inline value-range editor state for the selected measure (colours stay fixed).
  const [rangeRules, setRangeRules] = useState<EditableRangeRule[]>([]);
  const rangeLoadedRef = useRef(false);

  // ── Dropdown data hooks ──
  const { data: cubeDefinitionDetails } = useGetCubeDefinitionDetails(cubeDefinitionId);
  // All cubes, unfiltered by subject.
  const { data: cubeDropdown, isLoading: isFetchingCube } = useGetFunctionDefinitionCubeDropdown();
  const { data: layerDropdown, isLoading: isFetchingLayer } = useGetLayerListDropdownByCube(
    sourceCube?.value,
  );
  const { data: timeIntervalDropdown, isLoading: isFetchingTimeInterval } = useGetCubeTimeInterval(
    sourceCube?.value,
  );
  const { data: cubeAttributeDropdown, isLoading: isFetchingCubeAttributes } =
    useGetInsightViewCubeAttributesDropdown(sourceCube?.value?.toString());
  const { data: cubeDetailDataOptions, isLoading: isFetchingDetailOptions } = useGetCubeDataDetailOptions(
    sourceCube?.value?.toString(),
  );
  const { data: targetSubjectDropdown, isLoading: isFetchingTargetSubject } = useSubjectDropdown();
  const { data: targetSubjectDetails } = useGetSubjectDetails(targetSubjectId);
  const { data: tableDefinitionDropdown, isLoading: isFetchingTableDefinition } =
    useGetFunctionDefinitionSubjectAttributeDropdown(targetSubjectId ?? targetSubject?.value?.toString() ?? null);
  const { data: effectiveCellColors, isFetching: isFetchingCellColors } = useGetEffectiveCellColors(
    open ? insightViewId : undefined,
  );
  const { data: viewCubeAttributes } = useGetStandardViewCubeAttributesDropdown(
    open && showCellColour ? cubeDefinitionId : null,
  );
  const { mutate: upsertRange, isLoading: isSavingRange } = useUpsertCellColorOverride(insightViewId);

  // ── Derived dropdown options ──
  const cubeOptions: AutoCompleteItem[] = useMemo(() => cubeDropdown?.data?.data ?? [], [cubeDropdown?.data]);
  const layerOptions: AutoCompleteItem[] = useMemo(() => layerDropdown?.data ?? [], [layerDropdown?.data]);
  const timeIntervalOptions: AutoCompleteItem[] = useMemo(
    () => timeIntervalDropdown?.data ?? [],
    [timeIntervalDropdown?.data],
  );
  const attributeOptions: AutoCompleteItem[] = useMemo(
    () => cubeAttributeDropdown?.data ?? [],
    [cubeAttributeDropdown?.data],
  );
  const timePeriodOptions: AutoCompleteItem[] = useMemo(
    () => cubeDetailDataOptions?.data?.timePeriodOptions ?? [],
    [cubeDetailDataOptions?.data],
  );
  const targetSubjectOptions: TargetSubjectOption[] = useMemo(
    () =>
      (targetSubjectDropdown?.data ?? []).map((item) => ({
        value: item.id,
        label: item.displayName || item.subject,
        metaDataId: item.metaData?.id,
      })),
    [targetSubjectDropdown?.data],
  );
  // Only numeric attributes are valid decision point targets.
  const tableDefinitionOptions: AutoCompleteItem[] = useMemo(
    () => (tableDefinitionDropdown?.data ?? []).filter((item) => NUMERIC_ATTRIBUTE_RULE_TYPES.has(item.type)),
    [tableDefinitionDropdown?.data],
  );

  // ── Cell colour rows ──
  const cellColorRows: EffectiveCellColorItem[] = useMemo(
    () => effectiveCellColors?.data ?? [],
    [effectiveCellColors?.data],
  );

  const usedCellColorAttributeIds: Set<string> = useMemo(() => {
    const used = new Set<string>();
    cellColorRows.forEach((item) => {
      item.cubeAttribute && used.add(item.cubeAttribute.value.toString());
    });
    return used;
  }, [cellColorRows]);

  // The effective cell colour entry for the view's currently selected measure.
  const selectedMeasureColor = useMemo(
    () => cellColorRows.find((item) => item.cubeAttribute?.value?.toString() === activeCubeAttributeId) ?? null,
    [cellColorRows, activeCubeAttributeId],
  );

  const activeMeasureLabel = useMemo(() => {
    if (selectedMeasureColor?.cubeAttribute?.label) return selectedMeasureColor.cubeAttribute.label;
    const match = viewCubeAttributes?.data?.find((a) => a.value?.toString() === activeCubeAttributeId);
    return match?.label ?? '';
  }, [selectedMeasureColor, viewCubeAttributes?.data, activeCubeAttributeId]);

  const canSaveRanges =
    rangeRules.length > 0 &&
    rangeRules.every((r) => {
      if (isBetweenRangeRule(r)) return r.minValue !== '' && r.maxValue !== '';
      if (r.attributeReference) return true;
      return r.value !== '';
    });

  const updateRangeRule = (index: number, patch: Partial<EditableRangeRule>) => {
    setRangeRules((prev) => prev.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  };

  // Apply several rule patches in one update (used by the simplified threshold editor).
  const updateRangeRulesByIndex = (patches: Array<{ index: number; patch: Partial<EditableRangeRule> }>) => {
    setRangeRules((prev) => {
      let next = prev;
      patches.forEach(({ index, patch }) => {
        next = next.map((rule, i) => (i === index ? { ...rule, ...patch } : rule));
      });
      return next;
    });
  };

  // If the rules form a traffic-light ladder, edit them as shared breakpoints.
  const bandLadder = useMemo(() => detectBandLadder(rangeRules), [rangeRules]);

  const handleSaveRanges = () => {
    if (!activeCubeAttributeId || !canSaveRanges) return;
    const rules: EffectiveCellColorRuleItem[] = rangeRules.map((r, idx) => {
      const between = isBetweenRangeRule(r);
      return {
        id: r.id ?? `RULE-${crypto.randomUUID()}`,
        value: between || r.value === '' ? null : Number(r.value),
        minValue: between && r.minValue !== '' ? Number(r.minValue) : null,
        maxValue: between && r.maxValue !== '' ? Number(r.maxValue) : null,
        includeMin: between ? r.includeMin : null,
        includeMax: between ? r.includeMax : null,
        priorityOrder: idx,
        condition: r.condition,
        color: r.color,
        attributeReference: r.attributeReference,
      };
    });
    upsertRange(
      { cubeAttributeId: activeCubeAttributeId, body: { overrideState: 'OVERRIDE', rules } },
      {
        onSuccess: () => {
          toast.success('Cell colour updated');
          onClose();
        },
        onError: () => {
          toast.error('Failed to update cell colour');
        },
      },
    );
  };

  // ── Reset everything when the modal closes ──
  useEffect(() => {
    if (!open) {
      setSourceCube(null);
      setSourceLayer(null);
      setSourceTimeInterval(null);
      setSourceAttribute(null);
      setSourceTimePeriod(null);
      setTargetSubject(null);
      setTargetTableDefinition(null);
      setShowPayload(false);
      setCopied(false);
      setCellColorModal({ visible: false });
      setRangeRules([]);
      rangeLoadedRef.current = false;
    }
  }, [open]);

  // Load the selected measure's rules into the inline range editor once per open.
  useEffect(() => {
    if (!open || !showCellColour || rangeLoadedRef.current || isFetchingCellColors) return;
    const rules = selectedMeasureColor?.rules ?? [];
    setRangeRules(
      [...rules].sort((a, b) => a.priorityOrder - b.priorityOrder).map(toEditableRangeRule),
    );
    rangeLoadedRef.current = true;
  }, [open, showCellColour, isFetchingCellColors, selectedMeasureColor]);

  // ── Preselect source cube from the current insight view ──
  useEffect(() => {
    if (open && cubeDefinitionDetails?.data && !sourceCube) {
      const details = cubeDefinitionDetails.data;
      setSourceCube({ value: details.id, label: details.cubeName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cubeDefinitionDetails?.data]);

  // ── Auto select first option once loaded (mirrors CubeDataEntryPanel behavior) ──
  useEffect(() => {
    if (open && !sourceLayer && layerOptions.length > 0) setSourceLayer(layerOptions[0]);
  }, [open, sourceLayer, layerOptions]);

  useEffect(() => {
    if (open && !sourceTimeInterval && timeIntervalOptions.length > 0) setSourceTimeInterval(timeIntervalOptions[0]);
  }, [open, sourceTimeInterval, timeIntervalOptions]);

  useEffect(() => {
    if (open && !sourceAttribute && attributeOptions.length > 0) setSourceAttribute(attributeOptions[0]);
  }, [open, sourceAttribute, attributeOptions]);

  useEffect(() => {
    if (open && !sourceTimePeriod && timePeriodOptions.length > 0) setSourceTimePeriod(timePeriodOptions[0]);
  }, [open, sourceTimePeriod, timePeriodOptions]);

  // ── Cascade resets ──
  const handleSourceCubeChange = (_: SyntheticEvent, value: AutoCompleteItem | null) => {
    setSourceCube(value);
    setSourceLayer(null);
    setSourceTimeInterval(null);
    setSourceAttribute(null);
    setSourceTimePeriod(null);
  };

  const handleTargetSubjectChange = (_: SyntheticEvent, value: TargetSubjectOption | null) => {
    setTargetSubject(value);
    setTargetTableDefinition(null);
  };

  const isComplete = Boolean(
    (targetSubjectId || targetSubject) &&
      targetTableDefinition &&
      sourceCube &&
      sourceLayer &&
      sourceTimeInterval &&
      sourceAttribute &&
      sourceTimePeriod,
  );

  const payloadJson = useMemo(() => {
    const payload: DecisionPointPayload = {
      target: {
        subjectId:
          targetSubjectId || targetSubject?.metaDataId?.toString() || targetSubject?.value?.toString() || '',
        tableDefinitionId: targetTableDefinition?.value?.toString() || '',
      },
      source: {
        cubeAttributeId: sourceAttribute?.value?.toString() || '',
        cubeDefinitionId: sourceCube?.value?.toString() || '',
        cubeLayerId: sourceLayer?.value?.toString() || '',
        timeIntervalId: sourceTimeInterval?.value?.toString() || '',
        timePeriodValue: sourceTimePeriod?.value?.toString() || '',
      },
    };
    return JSON.stringify(payload, null, 2);
  }, [
    targetSubjectId,
    targetSubject,
    targetTableDefinition,
    sourceAttribute,
    sourceCube,
    sourceLayer,
    sourceTimeInterval,
    sourceTimePeriod,
  ]);

  const handleCopyPayload = () => {
    navigator.clipboard.writeText(payloadJson);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const targetTableName = targetSubjectId
    ? targetSubjectDetails?.data?.displayName || targetSubjectDetails?.data?.subject || 'this table'
    : targetSubject?.label;

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Stack sx={modalContentStyle} spacing={2}>
        {/* ── Header ── */}
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Stack direction='row' alignItems='center' spacing={1.25}>
            <NewReleasesIcon sx={{ color: '#42BB93' }} />
            <Stack spacing={0}>
              <Typography variant='body1' fontWeight='bold' sx={{ color: '#3B4797', lineHeight: 1.2 }}>
                Decision Point
              </Typography>
              <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                {showCellColour
                  ? 'Manage cell colour rules for this view'
                  : 'Update a column in this table with data from a cube'}
              </Typography>
            </Stack>
          </Stack>
          <IconButton size='small' onClick={onClose}>
            <HighlightOffRoundedIcon sx={{ color: '#98A2AE' }} />
          </IconButton>
        </Stack>
        <Divider />

        {!showCellColour && (
        <>
        {/* ── Purpose banner ── */}
        <Stack
          direction='row'
          spacing={1.25}
          sx={{
            p: 1.5,
            bgcolor: 'rgba(66, 187, 147, 0.06)',
            border: '1px solid rgba(66, 187, 147, 0.3)',
            borderRadius: 1,
          }}
        >
          <InfoOutlinedIcon fontSize='small' sx={{ color: '#42BB93', mt: '1px' }} />
          <Typography variant='body2' sx={{ color: '#475467' }}>
            A decision point writes cube results into your table. Pick the cube measure and time period to read
            from, then choose which column of{' '}
            <Box component='span' sx={{ fontWeight: 700, color: '#3B4797' }}>
              {targetTableName || 'the target table'}
            </Box>{' '}
            it should update.
          </Typography>
        </Stack>

        {/* ── Source → Target flow ── */}
        <Stack direction='row' alignItems='stretch'>
        <Stack spacing={1.5} sx={{ ...sectionBoxStyle, flex: 1.35 }}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <Box sx={stepBadgeStyle}>1</Box>
            <ViewInArOutlinedIcon fontSize='small' sx={{ color: '#3B4797' }} />
            <Stack spacing={0}>
              <Typography variant='body2' fontWeight={700} sx={{ color: '#3B4797', lineHeight: 1.2 }}>
                Source
              </Typography>
              <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                Where the values come from
              </Typography>
            </Stack>
          </Stack>
          <Stack direction='row' spacing={2}>
            <Autocomplete
              size='small'
              sx={filterAutoCompleteStyle}
              options={cubeOptions}
              value={sourceCube}
              onChange={handleSourceCubeChange}
              getOptionLabel={(option: AutoCompleteItem) => option.label || ''}
              isOptionEqualToValue={isOptionEqual}
              clearIcon={null}
              renderInput={(params) => <TextField {...params} label={isFetchingCube ? 'Loading...' : 'Cube'} />}
            />
          </Stack>
          <Stack direction='row' spacing={2}>
            <Autocomplete
              size='small'
              sx={filterAutoCompleteStyle}
              options={layerOptions}
              value={sourceLayer}
              onChange={(_, value) => setSourceLayer(value)}
              getOptionLabel={(option: AutoCompleteItem) => option.label || ''}
              isOptionEqualToValue={isOptionEqual}
              disabled={!sourceCube}
              clearIcon={null}
              renderInput={(params) => <TextField {...params} label={isFetchingLayer ? 'Loading...' : 'Layer'} />}
            />
            <Autocomplete
              size='small'
              sx={filterAutoCompleteStyle}
              options={timeIntervalOptions}
              value={sourceTimeInterval}
              onChange={(_, value) => setSourceTimeInterval(value)}
              getOptionLabel={(option: AutoCompleteItem) => option.label || ''}
              isOptionEqualToValue={isOptionEqual}
              disabled={!sourceCube}
              clearIcon={null}
              renderInput={(params) => (
                <TextField {...params} label={isFetchingTimeInterval ? 'Loading...' : 'Time Interval'} />
              )}
            />
          </Stack>
          <Stack direction='row' spacing={2}>
            <Autocomplete
              size='small'
              sx={filterAutoCompleteStyle}
              options={attributeOptions}
              value={sourceAttribute}
              onChange={(_, value) => setSourceAttribute(value)}
              getOptionLabel={(option: AutoCompleteItem) => option.label || ''}
              isOptionEqualToValue={isOptionEqual}
              disabled={!sourceCube}
              clearIcon={null}
              renderInput={(params) => (
                <TextField {...params} label={isFetchingCubeAttributes ? 'Loading...' : 'Measure'} />
              )}
            />
            <Autocomplete
              size='small'
              sx={filterAutoCompleteStyle}
              options={timePeriodOptions}
              value={sourceTimePeriod}
              onChange={(_, value) => setSourceTimePeriod(value)}
              getOptionLabel={(option: AutoCompleteItem) => option.label || ''}
              isOptionEqualToValue={isOptionEqual}
              disabled={!sourceCube}
              clearIcon={null}
              renderInput={(params) => (
                <TextField {...params} label={isFetchingDetailOptions ? 'Loading...' : 'Time Period'} />
              )}
            />
          </Stack>
        </Stack>

        <Stack alignItems='center' justifyContent='center' sx={{ px: 1.25 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: 'rgba(66, 187, 147, 0.1)',
              border: '1px solid rgba(66, 187, 147, 0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EastIcon fontSize='small' sx={{ color: '#42BB93' }} />
          </Box>
        </Stack>

        {/* ── Target card ── */}
        <Stack spacing={1.5} sx={{ ...sectionBoxStyle, flex: 1 }}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <Box sx={stepBadgeStyle}>2</Box>
            <TableChartOutlinedIcon fontSize='small' sx={{ color: '#3B4797' }} />
            <Stack spacing={0}>
              <Typography variant='body2' fontWeight={700} sx={{ color: '#3B4797', lineHeight: 1.2 }}>
                Target
              </Typography>
              <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                The column that will be updated
              </Typography>
            </Stack>
          </Stack>

          {targetSubjectId ? (
            <Stack
              direction='row'
              alignItems='center'
              spacing={0.75}
              sx={{ px: 1.25, py: 0.75, bgcolor: '#EDF2FF', borderRadius: 1 }}
            >
              <TableChartOutlinedIcon sx={{ fontSize: 16, color: '#3B4797' }} />
              <Typography variant='body2' sx={{ color: '#3B4797', fontWeight: 600 }}>
                {targetTableName}
              </Typography>
            </Stack>
          ) : (
            <Autocomplete
              size='small'
              sx={filterAutoCompleteStyle}
              options={targetSubjectOptions}
              value={targetSubject}
              onChange={handleTargetSubjectChange}
              getOptionLabel={(option: TargetSubjectOption) => option.label || ''}
              isOptionEqualToValue={isOptionEqual}
              clearIcon={null}
              renderInput={(params) => (
                <TextField {...params} label={isFetchingTargetSubject ? 'Loading...' : 'Subject'} />
              )}
            />
          )}
          <Autocomplete
            size='small'
            sx={filterAutoCompleteStyle}
            options={tableDefinitionOptions}
            value={targetTableDefinition}
            onChange={(_, value) => setTargetTableDefinition(value)}
            getOptionLabel={(option: AutoCompleteItem) => option.label || ''}
            isOptionEqualToValue={isOptionEqual}
            disabled={!targetSubjectId && !targetSubject}
            clearIcon={null}
            renderInput={(params) => (
              <TextField {...params} label={isFetchingTableDefinition ? 'Loading...' : 'Column to Update'} />
            )}
          />
          <Typography variant='caption' sx={{ color: '#98A2AE' }}>
            Only numeric columns are available for a decision point.
          </Typography>
        </Stack>
        </Stack>

        {/* ── Decision summary ── */}
        <Box sx={{ px: 1.5, py: 1.25, border: '1px dashed #C8D7EC', borderRadius: 1 }}>
          <Typography
            variant='caption'
            sx={{ color: '#98A2AE', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}
          >
            Decision Summary
          </Typography>
          <Typography variant='body2' sx={{ color: '#475467', mt: 0.25 }}>
            Update column <SummaryToken label={targetTableDefinition?.label} /> in{' '}
            <SummaryToken label={targetTableName} /> with measure <SummaryToken label={sourceAttribute?.label} />{' '}
            from cube <SummaryToken label={sourceCube?.label} /> (layer <SummaryToken label={sourceLayer?.label} />,{' '}
            <SummaryToken label={sourceTimeInterval?.label} /> period <SummaryToken label={sourceTimePeriod?.label} />
            ).
          </Typography>
        </Box>

        {/* ── Generated payload preview ── */}
        {showPayload && (
          <Box sx={{ position: 'relative', bgcolor: '#1E293B', borderRadius: 1, px: 1.5, py: 1.25 }}>
            <Typography
              variant='caption'
              sx={{ color: '#94A3B8', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}
            >
              Decision Payload
            </Typography>
            <Tooltip title={copied ? 'Copied!' : 'Copy JSON'}>
              <IconButton size='small' onClick={handleCopyPayload} sx={{ position: 'absolute', top: 6, right: 6 }}>
                <ContentCopyRoundedIcon fontSize='small' sx={{ color: copied ? '#42BB93' : '#94A3B8' }} />
              </IconButton>
            </Tooltip>
            <Typography
              component='pre'
              sx={{
                m: 0,
                mt: 0.5,
                fontFamily: 'monospace',
                fontSize: 12,
                color: '#E2E8F0',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                maxHeight: 220,
                overflowY: 'auto',
              }}
            >
              {payloadJson}
            </Typography>
          </Box>
        )}
        </>
        )}

        {showCellColour && (
        <>
        {/* ── Purpose banner ── */}
        <Stack
          direction='row'
          spacing={1.25}
          sx={{
            p: 1.5,
            bgcolor: 'rgba(66, 187, 147, 0.06)',
            border: '1px solid rgba(66, 187, 147, 0.3)',
            borderRadius: 1,
          }}
        >
          <InfoOutlinedIcon fontSize='small' sx={{ color: '#42BB93', mt: '1px' }} />
          <Typography variant='body2' sx={{ color: '#475467' }}>
            Fine-tune the value ranges that colour{' '}
            <Box component='span' sx={{ fontWeight: 700, color: '#3B4797' }}>
              {activeMeasureLabel || 'the selected measure'}
            </Box>{' '}
            in this view. Colours come from the view's definition. Use Advanced Configuration to change colours,
            conditions or priority.
          </Typography>
        </Stack>

        {/* ── Selected measure & value ranges ── */}
        <Stack spacing={1.5} sx={sectionBoxStyle}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Stack direction='row' alignItems='center' spacing={1}>
              <PaletteOutlinedIcon fontSize='small' sx={{ color: '#3B4797' }} />
              <Stack spacing={0}>
                <Typography variant='body2' fontWeight={700} sx={{ color: '#3B4797', lineHeight: 1.2 }}>
                  Selected Measure
                </Typography>
                <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                  Adjust when each colour applies
                </Typography>
              </Stack>
            </Stack>
            <Stack direction='row' alignItems='center' spacing={1}>
              <Stack
                direction='row'
                alignItems='center'
                spacing={0.75}
                sx={{ px: 1.25, py: 0.75, bgcolor: '#EDF2FF', borderRadius: 1 }}
              >
                <ViewInArOutlinedIcon sx={{ fontSize: 16, color: '#3B4797' }} />
                <Typography variant='body2' sx={{ color: '#3B4797', fontWeight: 600 }}>
                  {activeMeasureLabel || 'No measure selected'}
                </Typography>
              </Stack>
             
            </Stack>
          </Stack>

          {isFetchingCellColors ? (
            <Stack spacing={1}>
              <Skeleton variant='rounded' height={48} />
              <Skeleton variant='rounded' height={48} />
            </Stack>
          ) : rangeRules.length === 0 ? (
            <Stack alignItems='center' spacing={0.75} sx={{ py: 3.5 }}>
              <PaletteOutlinedIcon sx={{ fontSize: 34, color: '#C8D7EC' }} />
              <Typography variant='body2' fontWeight={600} sx={{ color: '#475467' }}>
                No colour rules for this measure
              </Typography>
              <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                Use Advanced Configuration to add colour rules.
              </Typography>
            </Stack>
          ) : bandLadder ? (
            (() => {
              // Editing a breakpoint updates the shared boundary of both adjacent bands.
              const handleBreakpoint = (i: number, v: string) => {
                const upperBand = bandLadder[i];
                const lowerBand = bandLadder[i + 1];
                updateRangeRulesByIndex([
                  { index: upperBand.index, patch: upperBand.kind === 'top' ? { value: v } : { minValue: v } },
                  { index: lowerBand.index, patch: lowerBand.kind === 'bottom' ? { value: v } : { maxValue: v } },
                ]);
              };
              return (
                <Stack spacing={1}>
                  {bandLadder.map((band, j) =>
                    j < bandLadder.length - 1 ? (
                      // Each band except the last owns the threshold below it.
                      <CellColorBandRow key={band.rule.id ?? band.index} color={band.rule.color}>
                        <Typography variant='body2' sx={{ color: '#98A2AE' }}>
                          value above
                        </Typography>
                        <TextField
                          size='small'
                          value={bandLowerBound(band.rule)}
                          onChange={(e) => handleBreakpoint(j, e.target.value)}
                          placeholder='0'
                          sx={{ width: 100 }}
                        />
                      </CellColorBandRow>
                    ) : (
                      // The last band is the catch-all for everything below the final threshold.
                      <CellColorBandRow key={band.rule.id ?? band.index} color={band.rule.color}>
                        <Typography variant='body2' sx={{ color: '#98A2AE', fontStyle: 'italic' }}>
                          {bandUpperBound(band.rule)
                            ? `everything below ${bandUpperBound(band.rule)}`
                            : 'everything else'}
                        </Typography>
                      </CellColorBandRow>
                    ),
                  )}
                </Stack>
              );
            })()
          ) : (
            <Stack spacing={1}>
              {rangeRules.map((rule, idx) => {
                const hexa = rule.color ? MAP_COLOR_CODE_WITH_COLOR_HEXA.get(rule.color.value.toString()) : undefined;
                const between = isBetweenRangeRule(rule);
                const phrase = rule.condition
                  ? CONDITION_PHRASE[rule.condition.value as string] ?? rule.condition.label
                  : '';
                return (
                  <Stack
                    key={rule.id ?? idx}
                    direction='row'
                    alignItems='center'
                    spacing={1}
                    sx={{ px: 1.5, py: 1, bgcolor: '#fff', border: '1px solid #E3EBF6', borderRadius: 1 }}
                  >
                    
                    <Tooltip title={rule.color?.label ?? ''}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: hexa ?? '#E0E0E0',
                          border: '1px solid rgba(0, 0, 0, 0.15)',
                          flexShrink: 0,
                        }}
                      />
                    </Tooltip>
                    <Typography variant='body2' sx={{ color: '#475467', fontWeight: 600, minWidth: 56 }}>
                      {rule.color?.label}
                    </Typography>

                    <Stack direction='row' alignItems='center' spacing={0.75} flex={1} justifyContent='flex-end'>
                      {between ? (
                        <>
                          <Typography variant='body2' sx={{ color: '#98A2AE' }}>
                            value between
                          </Typography>
                          <TextField
                            size='small'
                            value={rule.minValue}
                            onChange={(e) => updateRangeRule(idx, { minValue: e.target.value })}
                            placeholder='min'
                            sx={{ width: 92 }}
                          />
                          <Typography variant='body2' sx={{ color: '#98A2AE' }}>
                            and
                          </Typography>
                          <TextField
                            size='small'
                            value={rule.maxValue}
                            onChange={(e) => updateRangeRule(idx, { maxValue: e.target.value })}
                            placeholder='max'
                            sx={{ width: 92 }}
                          />
                        </>
                      ) : rule.attributeReference ? (
                        <Typography variant='body2' sx={{ color: '#475467' }}>
                          value {phrase}{' '}
                          <Box component='span' sx={{ fontWeight: 600, fontStyle: 'italic' }}>
                            {rule.attributeReference.label}
                          </Box>
                        </Typography>
                      ) : (
                        <>
                          <Typography variant='body2' sx={{ color: '#98A2AE' }}>
                            value {phrase}
                          </Typography>
                          <TextField
                            size='small'
                            value={rule.value}
                            onChange={(e) => updateRangeRule(idx, { value: e.target.value })}
                            placeholder='0'
                            sx={{ width: 110 }}
                          />
                        </>
                      )}
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          )}

          {activeMeasureLabel && (
            <Stack direction='row' justifyContent='flex-end'>
              <Button
                size='small'
                variant='text'
                startIcon={<TuneOutlinedIcon />}
                onClick={() =>
                  setCellColorModal({ visible: true, data: selectedMeasureColor ?? undefined })
                }
                sx={{ textTransform: 'none', color: '#3B4797' }}
              >
                Advanced Configuration
              </Button>
            </Stack>
          )}
        </Stack>
        </>
        )}

        <Divider />
        <Stack direction='row' spacing={1.5} justifyContent='space-between' alignItems='center'>
          {!showCellColour ? (
            <Stack direction='row' alignItems='center' spacing={1}>
              <Button
                size='small'
                variant='main-table-panel-border'
                startIcon={<AccountTreeOutlinedIcon />}
                onClick={() => setDataFlowModalOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Advanced
              </Button>
              <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                Define the Data Flow
              </Typography>
            </Stack>
          ) : (
            <Box />
          )}
          <Stack direction='row' spacing={1.5}>
            <Button variant='main-table-panel-border' onClick={onClose} sx={{ textTransform: 'none' }}>
              {showCellColour ? 'Close' : 'Cancel'}
            </Button>
            {showCellColour ? (
              <ButtonLoading
                variant='main-table-panel'
                loading={isSavingRange}
                disabled={!canSaveRanges || isSavingRange}
                onClick={handleSaveRanges}
                sx={{ textTransform: 'none' }}
              >
                Display
              </ButtonLoading>
            ) : (
              <Button
                variant='main-table-panel'
                disabled={!isComplete}
                onClick={() => setShowPayload(true)}
                sx={{ textTransform: 'none' }}
              >
                Save
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
      </Modal>

      <ModalCellColorOverride
        visible={cellColorModal.visible}
        onClose={() => {
          setCellColorModal({ visible: false });
          // Reload the inline range editor from refetched data after advanced edits.
          rangeLoadedRef.current = false;
        }}
        insightViewId={insightViewId}
        cubeDefinitionId={cubeDefinitionId}
        selectedData={cellColorModal.data}
        usedCubeAttributeIds={usedCellColorAttributeIds}
        initialCubeAttributeId={activeCubeAttributeId}
      />

      <ModalDataFlowDefinition
        open={dataFlowModalOpen}
        onClose={() => setDataFlowModalOpen(false)}
        targetSubjectId={targetSubjectId}
        initialDraft={{
          cube: sourceCube,
          layer: sourceLayer,
          timeInterval: sourceTimeInterval,
          measure: sourceAttribute,
          timePeriod: sourceTimePeriod,
          tableDefinition: targetTableDefinition,
        }}
      />
    </>
  );
}
